import { z, type ZodSchema, type ZodTypeDef } from 'zod'

/** Parse an value from a given schema with optional error or null return */
export function schemaModel<T, I = any>(
  data: I,
  schema: ZodSchema<T, ZodTypeDef, any>,
  throwError?: true,
): T
export function schemaModel<T, I = any>(
  data: I,
  schema: ZodSchema<T, ZodTypeDef, any>,
  throwError?: false,
): T | null
export function schemaModel<T, I = any>(
  data: I,
  schema: ZodSchema<T, ZodTypeDef, any>,
  throwError = true,
) {
  const result = schema.safeParse(data)

  if (!result.success) {
    console.error('Zod Schema Error')
    console.group()
    result.error.issues.forEach((issue) => {
      console.log(`Path: ${issue.path.join(', ')} \nError: ${issue.message}`)
    })

    console.groupEnd()
  }

  if (throwError && !result.success) throw new Error('Zod validation failure')

  return result.success ? result.data : null
}

export const emailSchema = z
  .string()
  .email()
  .transform((a) => a.toLowerCase())

export const timestampSchema = z.number().int().min(0)
export const nanoidSchema = z.string().min(5)

/** Unix timestamp in seconds. Default to current time */
export function unixTimestamp(
  /** Javascript date or timestamp in milliseconds */
  date: Date | number | string = Date.now(),
) {
  return Math.floor(new Date(date).getTime() / 1000)
}

export enum Environments {
  Dev = 'development',
  Test = 'test',
  Prod = 'production',
  Staging = 'staging',
}

export type DeployEnvironments =
  | 'development'
  | 'test'
  | 'staging'
  | 'production'

export const deployEnvironmentsSchema = z
  .nativeEnum(Environments)
  .optional()
  .default(Environments.Dev)

/**
 * This RegEx is valid as of 2024-11-06.
 * If it reports false positives or false negatives, please check the website for an update!
 * See here: https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
 *
 * Uses numbered capture groups for better javascript support
 */
export const regexSemverNumberedGroups =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/

export const semverSchema = z.string().regex(regexSemverNumberedGroups, {
  message: 'Version must be a valid semver format (e.g. 0.0.1)',
})

/** Convert an array of objects to an object with their uid as the key */
export function arrayToMap<T extends { uid: string }>(array: T[]) {
  return array.reduce<Record<string, T>>((acc, item) => {
    acc[item.uid] = item
    return acc
  }, {})
}

/** Unwrap a complex type to reveal the underlying primitive types */
export type Prettify<T> = {
  [K in keyof T]: T[K]
} & {}

/**
 * RegEx for slug validation
 *
 * Valid Examples:
 * monkey-climbs-trees, kangaroo--jumps---around
 *
 * Invalid Examples:
 * 1test-example.com, -test, example-
 */
export const regexSlug = /^[a-z](?:[a-z0-9-]*[a-z0-9])?$/

/**
 * Zod Schema for slug validation, with a reasonable max length.
 */
export const slugSchema = z
  .string()
  .min(1, 'Slug is required')
  .min(3, 'Slug must be at least 3 characters')
  .max(60, 'Slug must be less than 60 characters')
  .regex(regexSlug, {
    message:
      'Slug can have lowercase letters, digits, or hyphens. It must start with a lowercase letter and end with a letter or number.',
  })

const setPreprocess = (input: unknown) =>
  input instanceof Set ? input : Array.isArray(input) ? new Set(input) : input

const setTransform = (set: Set<string>) => Array.from(set)

export const scalarSet = <T extends z.ZodTypeAny>(zodSetType: z.ZodSet<T>) =>
  z.preprocess(setPreprocess, zodSetType).transform(setTransform)

/** See @scalar-org/helpers/url/slugify.ts */
export const slugify = (v: string) =>
  v
    .normalize('NFC')
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{M}\p{N}\s-]/gu, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255)

export const originSchema = z.string().transform((arg) =>
  arg
    .split(',')
    .map((s) => s.trim())
    .filter((s) => !!s)
    .map((arg) => {
      if (arg.at(0) !== '^') return arg

      // Attempt to convert to regexp
      try {
        return new RegExp(arg)
      } catch {
        return arg
      }
    }),
)

/**
 * Registry Namespace for a team.
 */
export const namespaceSchema = z
  .string()
  .regex(/^[a-zA-Z0-9-_]+$/)
  .toLowerCase()
  .min(3)
  .max(50)
